import { Button, Flex } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { PortfolioCoreBorrowingBaseChartTemplate } from '../shared/PortfolioCoreBorrowingBaseChartTemplate';
import { PortfolioStatementsContainer } from '../statements/PortfolioStatementsContainer';
import {
  chargeOffAndDelinquentOverviewChartConfig,
  collectionsAccountChartConfig,
  daysPastDueChartConfig,
} from './profitLossChartConfigs';
import { ProfitLossOverviewChartTemplate } from './ProfitLossOverviewChartTemplate';
import { ProfitLossVintageChartTemplate } from './ProfitLossVintageChartTemplate';
import { RepaymentsVintageChart } from './RepaymentsVintageChart';
import { z } from 'zod/v4';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from '~/packages/credit-facility/ui/CreditFacilitySelect';

// Constants
const PROFIT_LOSS_DISPLAY_MODE_QUERY_PARAM = 'display';

// Types
const ProfitLossDisplayModeKeysStruct = z
  .enum(['all', 'overview', 'vintage', 'statements'])
  .default('all');
type ProfitLossDisplayModeKeys = z.infer<
  typeof ProfitLossDisplayModeKeysStruct
>;

type ProfitLossContainerProps = { creditFacilityId: string };
export const ProfitLossContainer = ({
  creditFacilityId,
}: ProfitLossContainerProps) => {
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const query = useQueryParams();
  const queryDisplayMode = query.get(PROFIT_LOSS_DISPLAY_MODE_QUERY_PARAM);
  const result = ProfitLossDisplayModeKeysStruct.safeParse(queryDisplayMode);
  const displayMode = result.success ? result.data : 'all';

  const onSetDisplayMode = (selectedDisplayMode: ProfitLossDisplayModeKeys) => {
    const covenantsBaseRoute = '/portfolio/profit-loss';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${PROFIT_LOSS_DISPLAY_MODE_QUERY_PARAM}=${selectedDisplayMode}`;

    navigate(`${covenantsBaseRoute}${queryParams}`);
  };
  // If feature flag is enabled, only show statements
  if (flags.isProfitLossStatementsOnlyEnabled) {
    return (
      <Flex direction="column" gap="2.5rem">
        <PortfolioStatementsContainer creditFacilityId={creditFacilityId} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2.5rem">
      <Flex gap="0.5rem">
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'all'}
          onClick={() => onSetDisplayMode('all')}
        >
          Show All
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'overview'}
          onClick={() => onSetDisplayMode('overview')}
        >
          Overview
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'vintage'}
          onClick={() => onSetDisplayMode('vintage')}
        >
          By Vintage
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'statements'}
          onClick={() => onSetDisplayMode('statements')}
        >
          Statements
        </Button>
      </Flex>
      {(displayMode === 'all' || displayMode === 'overview') && (
        <>
          <ProfitLossOverviewChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Charge Off & Delinquent"
            chartConfig={chargeOffAndDelinquentOverviewChartConfig}
          />
          <ProfitLossOverviewChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Days Past Due"
            chartConfig={daysPastDueChartConfig}
          />
          <PortfolioCoreBorrowingBaseChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Collections Account"
            chartConfig={collectionsAccountChartConfig}
          />
        </>
      )}
      {(displayMode === 'all' || displayMode === 'vintage') && (
        <Flex direction="column" gap="1rem">
          <Typography variant="section-title" trim>
            By Vintage
          </Typography>
          <RepaymentsVintageChart creditFacilityId={creditFacilityId} />
          <ProfitLossVintageChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Charge Off By Vintage"
            field="chargedOffPercentageOfTotalVintageCalcValue"
            fieldType="percentage"
            yAxisLabel="Percent of Total Exposure"
          />
          <ProfitLossVintageChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Delinquency By Vintage"
            field="delinquentPercentageOfTotalVintageCalcValue"
            fieldType="percentage"
            yAxisLabel="Percent of Total Exposure"
          />
        </Flex>
      )}
      {(displayMode === 'all' || displayMode === 'statements') && (
        <PortfolioStatementsContainer creditFacilityId={creditFacilityId} />
      )}
    </Flex>
  );
};
